/**
 * TrainingRoot — orchestrator mounted inside MainLayout. Renders the
 * persistent banner + topic picker modal + floating Claude panel, and
 * wires the "Train me on this page" quick action (banner button + ⌘K/
 * Ctrl+K keyboard shortcut).
 *
 * Keyboard shortcut behavior:
 *   - Training Mode OFF: shortcut is NOT captured (preserves
 *     UniversalSearch's existing Cmd+K on Dashboard).
 *   - Training Mode ON: Cmd/Ctrl+K resolves the current URL to a topic
 *     slug, fetches the topic, and starts the guided walkthrough.
 */
import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTraining } from "@/lib/trainingContext";
import { getTopic } from "@/lib/trainingApi";
import { resolveTopicForPath } from "@/lib/trainingRouteMap";
import TrainingBanner from "@/components/training/TrainingBanner";
import TrainingTopicPicker from "@/components/training/TrainingTopicPicker";
import TrainingFloatingPanel from "@/components/training/TrainingFloatingPanel";
import WalkthroughController from "@/components/training/WalkthroughController";
import { toast } from "sonner";

export default function TrainingRoot() {
  const { enabled, setEnabled, setActiveTopic, setPickerOpen } = useTraining();
  const { pathname } = useLocation();

  const handlePickTopic = useCallback((topic) => {
    setActiveTopic(topic);
    setPickerOpen(false);
    toast.success(`Training: ${topic.title}`);
  }, [setActiveTopic, setPickerOpen]);

  const handleTrainMeOnPage = useCallback(async (suggestedSlug) => {
    const slug = suggestedSlug || resolveTopicForPath(pathname);
    try {
      const topic = await getTopic(slug);
      setActiveTopic(topic);
      setPickerOpen(false);
      toast.success(`Training: ${topic.title}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.warning("This page isn't available for your role — opening the topic picker.");
      } else if (status === 404) {
        toast.warning("No training for this page yet — try the picker.");
      } else {
        toast.error("Couldn't start training. Please try again.");
      }
      setPickerOpen(true);
    }
  }, [pathname, setActiveTopic, setPickerOpen]);

  // Expose exit handler globally so the banner/panel X buttons can
  // trigger it without extra prop drilling (they're rendered separately).
  useEffect(() => {
    window.__trainingExit = () => setEnabled(false);
    window.__trainingOpenPicker = () => setPickerOpen(true);
    return () => { delete window.__trainingExit; delete window.__trainingOpenPicker; };
  }, [setEnabled, setPickerOpen]);

  // Cmd/Ctrl+K keyboard shortcut — ONLY when Training Mode is ON.
  // This leaves UniversalSearch (Dashboard) untouched when training is OFF.
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && !e.shiftKey && (e.key === "k" || e.key === "K");
      if (!isCmdK) return;
      // Don't hijack when user is typing in an input/textarea that's not the trainer's own
      const tgt = e.target;
      const inInput = tgt && (tgt.tagName === "INPUT" || tgt.tagName === "TEXTAREA" || tgt.isContentEditable);
      const isTrainerInput = tgt?.getAttribute?.("data-testid") === "training-panel-input";
      if (inInput && !isTrainerInput) return;
      e.preventDefault();
      handleTrainMeOnPage();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled, handleTrainMeOnPage]);

  return (
    <>
      <TrainingBanner onTrainMePressed={handleTrainMeOnPage} />
      <TrainingTopicPicker onPickTopic={handlePickTopic} />
      <WalkthroughController />
      <TrainingFloatingPanel />
    </>
  );
}
